"use client";

import { useState, useRef, lazy, Suspense, useCallback, useEffect } from "react";
import { PostPlayer, type PostPlayerRef } from "@/components/audio/PostPlayer";
import { EllipsisMenu } from "@/components/post/EllipsisMenu";
import { DeleteConfirmModal } from "@/components/post/DeleteConfirmModal";
import { PostHeader } from "@/components/post/PostHeader";
import { type EncodedAudio } from "@/lib/audio/encoding";
import { useIdentity } from "@/lib/identity/useIdentity";
import { getLocalUserId } from "@/lib/identity/client";
import { filterDeletedLeafReplies, hasChildren, type DeletedByType } from "@/lib/tombstone";
import { replyCardStyles } from "@/lib/styles/post-card-styles";
import { X } from "lucide-react";

// Lazy load RecorderPanel since it's only shown when user wants to reply
const RecorderPanel = lazy(() => import("@/components/recording/RecorderPanel").then(mod => ({ default: mod.RecorderPanel })));

interface Reply {
  id: string;
  user_id: string;
  pseudonym: string;
  audio_url: string | null;
  duration_ms: number;
  created_at: string;
  parent_id: string;
  slug: string;
  replies?: Reply[];
  deleted_by_type?: DeletedByType; // Optional, defaults to 'user' if not provided
}

interface ReplyCardProps {
  reply: Reply;
  depth?: number;
  onPublish: (audio: EncodedAudio, parentId: string) => Promise<void>;
  parentPseudonym?: string;
  mainPostId: string;
  collapseAllTrigger?: number;
  onReport?: (postId: string, pseudonym: string) => void;
  highlightedReplyId?: string | null;
  onHighlightParent?: (parentId: string) => void;
  openRecorderId?: string | null;
  onOpenRecorder?: (id: string) => void | Promise<void>;
  onCloseRecorder?: () => void;
  currentUserId?: string | null;
  onDelete?: (replyId: string) => Promise<void>;
  isAdmin?: boolean; // When true, hides user actions (admin must logout to use user mode)
  onReplyPlay?: (replyId: string) => void; // Callback when reply audio starts playing
  rateLimitError?: string | null; // Rate limit error message for this specific reply
  getRateLimitError?: (replyId: string) => string | null; // Function to get rate limit error for nested replies
  onDismissRateLimitError?: (replyId: string) => void; // Callback to dismiss rate limit error for a specific reply
  onRecorderStateChange?: (replyId: string, state: "idle" | "recording" | "processing" | "preview" | "uploading") => void; // Callback when recorder state changes
}

/**
 * Flat reply card matching mockup design
 * Level-0: No indent, pink-accented border
 * Level-1: 8px indent with optional connector line
 */
export function ReplyCard({
  reply,
  depth = 0,
  onPublish,
  parentPseudonym,
  mainPostId,
  onReport,
  highlightedReplyId,
  onHighlightParent,
  openRecorderId,
  onOpenRecorder,
  onCloseRecorder,
  currentUserId,
  onDelete,
  isAdmin = false,
  onReplyPlay,
  rateLimitError,
  getRateLimitError,
  onDismissRateLimitError,
  onRecorderStateChange,
}: ReplyCardProps) {
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoScrollFiredRef = useRef(false);
  const recorderElementRef = useRef<HTMLDivElement | null>(null);
  const [recorderState, setRecorderState] = useState<"idle" | "recording" | "processing" | "preview" | "uploading">("idle");
  const { identity, loading, error, rerollPseudonym } = useIdentity();
  const [ellipsisMenuOpen, setEllipsisMenuOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const postPlayerRef = useRef<PostPlayerRef>(null);
  
  const isOwner = currentUserId === reply.user_id;
  
  // Flat feed: cap at 8px for all nested replies (depth 1+)
  const marginLeft = depth > 0 ? 8 : 0;

  // Check if this reply's recorder is open
  const showRecorder = openRecorderId === reply.id;

  // Helper function to scroll recorder into view
  const scrollRecorderIntoView = useCallback(() => {
    const el = recorderElementRef.current;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const padding = 32;

    if (rect.left < 0 || rect.right > viewportWidth) {
      return;
    }

    let delta = 0;
    if (rect.top < padding) {
      delta = rect.top - padding;
    } else if (rect.bottom > viewportHeight - padding) {
      delta = rect.bottom - (viewportHeight - padding);
    }

    if (delta !== 0) {
      window.scrollBy({ top: delta, behavior: "smooth" });
    }
  }, []);

  // Reset auto-scroll flag when recorder opens/closes (FR-6.9)
  useEffect(() => {
    if (showRecorder) {
      // Reset scroll flag when recorder opens
      autoScrollFiredRef.current = false;
      setRecorderState("idle");
    } else {
      // Reset scroll flag when recorder closes (ensures scroll fires once per open)
      autoScrollFiredRef.current = false;
      setRecorderState("idle");
    }
  }, [showRecorder]);

  // Auto-scroll when recorder opens/expands (FR-6.8)
  useEffect(() => {
    if (!showRecorder || autoScrollFiredRef.current) {
      return;
    }

    // Wait for DOM to update and RecorderPanel to mount (lazy loaded)
    requestAnimationFrame(() => {
      setTimeout(() => {
        scrollRecorderIntoView();
        setTimeout(scrollRecorderIntoView, 250);
        autoScrollFiredRef.current = true;
      }, 120);
    });
  }, [showRecorder, scrollRecorderIntoView, reply.id]);

  // Auto-scroll again when recording starts or preview appears (recorder height increases)
  useEffect(() => {
    if (!showRecorder) return;
    
    // Trigger auto-scroll when state changes to "recording" or "preview"
    if (recorderState === "recording" || recorderState === "preview") {
      // Wait a bit for the UI to update (timer/preview to render)
      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollRecorderIntoView();
        }, 150);
      });
    }
  }, [recorderState, showRecorder, scrollRecorderIntoView]);

  // Get deletedByType, defaulting to 'user' if not provided
  const deletedByType: DeletedByType = reply.deleted_by_type || 'user';

  // Check if this is a deleted leaf reply (audio_url is null AND no children)
  const isDeletedLeaf = reply.audio_url === null && !hasChildren(reply);
  
  // Only filter out user-deleted leaf replies (they're already filtered at parent level, but double-check here)
  // Moderator-deleted leaf replies should show tombstones, so we don't return null for them
  if (isDeletedLeaf && deletedByType === 'user') {
    return null;
  }

  // Check if this is a deleted parent reply (audio_url is null AND has children)
  // OR a moderator-deleted leaf reply (should show tombstone)
  const isDeletedParent = reply.audio_url === null && hasChildren(reply);
  const isModeratorDeletedLeaf = isDeletedLeaf && deletedByType === 'moderator';

  const handlePublish = async (audio: EncodedAudio) => {
    await onPublish(audio, reply.id);
    // Note: Don't call onCloseRecorder() here - handleReplyPublish already sets
    // setOpenRecorderId(null). Calling onCloseRecorder() causes a race condition
    // where stale closure values trigger the recording loss modal incorrectly.
  };

  // Memoized callback for recorder state changes to prevent infinite loops
  const handleRecorderStateChange = useCallback((state: "idle" | "recording" | "processing" | "preview" | "uploading") => {
    setRecorderState(state);
    onRecorderStateChange?.(reply.id, state);
  }, [reply.id, onRecorderStateChange]);

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const isHighlighted = highlightedReplyId === reply.id;

  const handleDeleteReply = async () => {
    // Stop audio immediately to prevent abort errors
    if (postPlayerRef.current) {
      postPlayerRef.current.pause();
    }

    setDeleting(true);
    setDeleteError(null);
    setDeleteSuccess(false);

    try {
      if (!onDelete) {
        throw new Error("Delete handler not available");
      }

      await onDelete(reply.id);
      
      // Show success message
      setDeleteSuccess(true);
      setTimeout(() => {
        setDeleteSuccess(false);
      }, 3000);
    } catch (err) {
      // Detect network errors and provide user-friendly message
      const isNetworkError = err instanceof TypeError && (
        err.message.includes('fetch failed') ||
        err.message.includes('Failed to fetch') ||
        err.message.includes('NetworkError') ||
        err.message.toLowerCase().includes('network')
      );
      
      const errorMessage = err instanceof Error ? err.message : "Failed to delete reply";
      
      // Treat "Unauthorized" as network error since user should be authorized if they can see delete button
      // (The API returns 403 for DB connection failures, but from user's perspective it's a connection issue)
      const isLikelyNetworkError = isNetworkError || errorMessage === "Unauthorized";
      
      const message = isLikelyNetworkError
        ? "Not able to connect. Please check your internet connection and try again."
        : errorMessage;
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  };
  
  return (
    <>
      {/* Main Reply Card - Mockup Exact Styling with top-left pink accent */}
      <article
        id={`reply-${reply.id}`}
        className={`relative ${replyCardStyles.container}`}
        style={{ 
          marginLeft: `${marginLeft}px`,
          background: isHighlighted
            ? replyCardStyles.backgroundHighlighted
            : replyCardStyles.background,
          borderColor: isHighlighted ? replyCardStyles.borderColorHighlighted : replyCardStyles.borderColor,
          boxShadow: isHighlighted ? replyCardStyles.shadowHighlighted : "none",
        }}
      >
        {/* Optional Connector Line for nested replies (depth 1+) */}
        {depth > 0 && (
          <div
            className="absolute bottom-3 left-[-4px] top-3 w-[2px] rounded-full"
            style={{
              background: "linear-gradient(to bottom, rgba(255, 62, 165, 0), rgba(255, 62, 165, 0.22), rgba(255, 62, 165, 0))",
            }}
          />
        )}

        {/* Reply Header */}
        <div className={`${replyCardStyles.headerMargin} flex items-center justify-between`}>
          <PostHeader
            pseudonym={reply.pseudonym}
            timestamp={formatTimeAgo(reply.created_at)}
            parentPseudonym={parentPseudonym}
            isReply={true}
            isMainPost={reply.parent_id === mainPostId}
            onMouseEnterParent={() => {
              // Clear any existing click timeout
              if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
                clickTimeoutRef.current = null;
              }
              onHighlightParent?.(reply.parent_id);
            }}
            onMouseLeaveParent={() => {
              // Only clear if there's no active click timeout
              if (!clickTimeoutRef.current) {
                onHighlightParent?.('');
              }
            }}
            onClickParent={() => {
              // Clear any existing timeout
              if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
              }
              
              onHighlightParent?.(reply.parent_id);
              
              // Scroll parent into view
              if (reply.parent_id === mainPostId) {
                // If parent is the main post, scroll to top
                window.scrollTo({ top: 0, behavior: 'smooth' });
                // Clear highlight after 2.25 seconds
                clickTimeoutRef.current = setTimeout(() => {
                  onHighlightParent?.('');
                  clickTimeoutRef.current = null;
                }, 2250);
              } else {
                // Find parent reply element
                const parentElement = document.getElementById(`reply-${reply.parent_id}`);
                if (parentElement) {
                  parentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  // Clear highlight after 2.25 seconds
                  clickTimeoutRef.current = setTimeout(() => {
                    onHighlightParent?.('');
                    clickTimeoutRef.current = null;
                  }, 2250);
                }
              }
            }}
          />
          {/* Only show ellipsis menu if reply is not deleted, user owns it, and not admin */}
          {/* Admin must logout to use user-level actions */}
          {isOwner && reply.audio_url !== null && !isAdmin && (
            <EllipsisMenu
              isOpen={ellipsisMenuOpen}
              onOpen={() => setEllipsisMenuOpen(true)}
              onClose={() => setEllipsisMenuOpen(false)}
              onDelete={() => setDeleteConfirmOpen(true)}
            />
          )}
        </div>

        {/* Audio Player or Tombstone */}
        <div className={replyCardStyles.audioPlayerContainer}>
          {isDeletedParent || isModeratorDeletedLeaf ? (
            <PostPlayer
              ref={postPlayerRef}
              audioUrl={null}
              durationMs={reply.duration_ms}
              contentType="reply"
              isOwner={isOwner}
              deletedByType={deletedByType}
            />
          ) : (
            <PostPlayer
              ref={postPlayerRef}
              audioUrl={reply.audio_url}
              durationMs={reply.duration_ms}
              onPlay={() => onReplyPlay?.(reply.id)}
            />
          )}
        </div>

        {/* Delete Success Message */}
        {deleteSuccess && (
          <div className="mb-2 rounded-2xl border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-400 transition-opacity duration-300">
            Reply deleted successfully
          </div>
        )}

        {/* Delete Error Message */}
        {deleteError && (
          <div className="mb-2 rounded-2xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
            {deleteError}
            <button
              onClick={() => setDeleteError(null)}
              className="ml-2 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Reply Actions - Only show if reply is not deleted and not in admin mode */}
        {!showRecorder && reply.audio_url !== null && !isAdmin && (
          <div className="mt-2 flex flex-col gap-2">
            {rateLimitError && (
              <div className="rounded-2xl border border-rose-400/20 bg-rose-500/5 p-3 text-sm text-rose-200/80">
                <div className="flex items-center justify-between">
                  <span>{rateLimitError}</span>
                  <button
                    onClick={() => {
                      onDismissRateLimitError?.(reply.id);
                    }}
                    className="ml-2 underline text-rose-200/60 hover:text-rose-200/80"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (onOpenRecorder) {
                    void onOpenRecorder(reply.id);
                  }
                }}
                className="rounded-full border border-[#262636] bg-[#0b0b14] px-2.5 py-1 text-xs text-[#a3a3c2] transition hover:bg-[#171726]"
              >
                Reply
              </button>
              {/* Only show report button if user doesn't own this reply */}
              {onReport && currentUserId !== reply.user_id && (
                <button
                  onClick={() => onReport(reply.id, reply.pseudonym)}
                  className="rounded-full border border-transparent px-2.5 py-1 text-[11px] text-[#a3a3c2] transition hover:border-[#262636] hover:bg-[#0a0a12]"
                >
                  Report
                </button>
              )}
            </div>
          </div>
        )}
      </article>

      {/* Inline Reply Recorder */}
      {showRecorder && (
        <div
          ref={recorderElementRef}
          id={`recorder-reply-${reply.id}`}
          className="mt-2.5 rounded-[10px] border border-[rgba(255,255,255,0.04)] bg-[rgba(255,255,255,0.05)] px-3 py-3"
          style={{ marginLeft: `${marginLeft}px` }}
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-base font-semibold">Record Your Reply</h4>
              <p className="mt-1 text-sm text-[#a3a3c2]">
                Replying to{" "}
                <span className="font-semibold text-pink-200">
                  @{reply.pseudonym}
                </span>
              </p>
            </div>
            {recorderState === "idle" && (
              <button
                onClick={onCloseRecorder}
                className="rounded-lg border border-[#262636] bg-[#0a0a12] p-2 text-[#a3a3c2] transition hover:border-[rgba(255,255,255,0.16)] hover:bg-[#141422]"
                title="Cancel"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <Suspense fallback={<div className="py-8 text-center text-sm text-[#a3a3c2]">Loading recorder...</div>}>
            <RecorderPanel 
              onPublish={handlePublish}
              submitButtonText="Send Reply"
              submitButtonTextLoading="Sending Reply…"
              onStateChange={handleRecorderStateChange}
            />
          </Suspense>
          
          {/* Inline Identity Line - Show only when idle/preview */}
          {(recorderState === "idle" || recorderState === "preview") && (
            <div
              className={`mt-3 flex items-center justify-center gap-1 text-xs text-slate-400 ${
                identity && !identity.can_reroll ? "opacity-50" : "opacity-65"
              }`}
            >
              <span>Posting as </span>
              {loading ? (
                <span className="font-medium text-slate-100">Generating...</span>
              ) : identity ? (
                <>
                  <span
                    className={`font-medium ${
                      identity.can_reroll ? "text-slate-100" : "text-slate-300"
                    }`}
                  >
                    {identity.pseudonym}
                  </span>
                  {identity.can_reroll && (
                    <>
                      <span> · </span>
                      <button
                        onClick={() => void rerollPseudonym()}
                        className="text-xs text-pink-400 hover:text-pink-300 underline-offset-2 hover:underline cursor-pointer"
                      >
                        Change
                      </button>
                    </>
                  )}
                </>
              ) : error ? (
                <span className="font-medium text-red-400">{error}</span>
              ) : (
                <span className="font-medium text-red-400">Failed to load</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Nested Replies - Flat Feed (Recursive but Visually Flat) */}
      {reply.replies && reply.replies.length > 0 && (
        <div className="mt-2.5 flex flex-col gap-2.5">
          {filterDeletedLeafReplies(reply.replies).map((childReply) => (
            <ReplyCard
              key={childReply.id}
              reply={childReply}
              depth={depth + 1}
              onPublish={onPublish}
              parentPseudonym={reply.pseudonym}
              mainPostId={mainPostId}
              onReport={onReport}
              highlightedReplyId={highlightedReplyId}
              onHighlightParent={onHighlightParent}
              openRecorderId={openRecorderId}
              onOpenRecorder={onOpenRecorder}
              onCloseRecorder={onCloseRecorder}
              currentUserId={currentUserId}
              onDelete={onDelete}
              isAdmin={isAdmin}
              onReplyPlay={onReplyPlay}
              rateLimitError={getRateLimitError ? getRateLimitError(childReply.id) : null}
              getRateLimitError={getRateLimitError}
              onDismissRateLimitError={onDismissRateLimitError}
              onRecorderStateChange={onRecorderStateChange}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteReply}
        contentType="reply"
      />
    </>
  );
}
